import { useMemo, useState } from 'react';
import { Button, Col, DatePicker, Form, Input, InputNumber, notification, Radio, Row, Tooltip } from 'antd';
import { CheckCircleOutlined, QuestionOutlined } from '@ant-design/icons';
import waistCircumference from '../../../assets/images/waist-circumference.png';
import { StyledModal, WaistCircumferenceImage, WaistCircumferenceTipInfo } from './style';
import { _FORM_ITEM_LAYOUT, _TAIL_FORM_ITEM_LAYOUT } from '../../../constants';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import dayjs from 'dayjs';
import { useUpdateProfessionalClientBio } from '../../../hooks/professionals/useClientGeneralInfo';
import { useGetProfessionalClient } from '../../../hooks/professionals/useProfessionalClient';
import { NotificationTitle } from '../../Common/Notification/style';
import { theme } from '../../../styles/theme';

const BioForm = () => {
  const [api, contextHolder] = notification.useNotification();
  const { selectedClient } = useSelector((state: RootState) => state.professionals);
  const { update: updateProfClientBio, loading: updateLoading } = useUpdateProfessionalClientBio();
  const { refetch: refetchClient } = useGetProfessionalClient(selectedClient?.id as string);
  const [form] = Form.useForm();
  const [waistCircumferenceModal, setWaistCircumferenceModal] = useState<boolean>(false);
  const defaultValues = useMemo(() => {
    const birthDateForPicker = dayjs(selectedClient?.bio.dateOfBirth);
    return {
      firstName: selectedClient?.firstName,
      familyName: selectedClient?.familyName,
      ...selectedClient?.bio,
      dateOfBirth: birthDateForPicker.isValid() ? birthDateForPicker : null,
    }
  }, [selectedClient]);

  const onFinish = async (values: any) => {
    try {
      const res = await updateProfClientBio(selectedClient?.id as string, values);
      if (res.professionalClientUpdateBio) {
        api.info({
          message: <NotificationTitle>Client Bio updates successfully</NotificationTitle>,
          placement: 'topRight',
          icon:
            <CheckCircleOutlined
              style={{
                color: theme.color.primary,
              }}
            />,
          style: {
            background: `${theme.bg.secondary}`,
            borderRadius: 10,
            alignItems: 'center',
            display: 'flex'
          }
        });
        return;
      }
    } catch (error: any) {
      console.error(error)
    } finally {
      refetchClient();
    }
  };

  const handleWaistCircumferenceModal = () => {
    setWaistCircumferenceModal(false);
  };

  return (
    <Form
      {..._FORM_ITEM_LAYOUT}
      form={form}
      name="bio"
      onFinish={onFinish}
      initialValues={{ ...defaultValues }}
      style={{ marginTop: '1rem' }}
      scrollToFirstError
    >
      {contextHolder}
      <Form.Item
        name="firstName"
        label="First name"
        labelAlign="left"
        rules={[
          {
            required: true,
            message: 'Please input your First name!',
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="familyName"
        label="Family name"
        labelAlign="left"
        rules={[
          {
            required: true,
            message: 'Please input your Family name!',
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="sex"
        label="Sex"
        labelAlign="left"
        rules={[{ required: true, message: 'Please input your sex!' }]}
      >
        <Radio.Group>
          <Radio value="male"> Male </Radio>
          <Radio value="female"> Female </Radio>
        </Radio.Group>
      </Form.Item>
      <Form.Item
        name="dateOfBirth"
        label="Date of birth"
        labelAlign="left"
        rules={[{ required: true, message: 'Please input your date of birth!' }]}
      >
        <DatePicker format="YYYY-MM-DD" />
      </Form.Item>
      <Form.Item
        name="weight"
        label="Weight"
        labelAlign="left"
        rules={[{ required: true, message: 'Please input your weight!' }]}
      >
        <InputNumber
          addonAfter="kg"
        />
      </Form.Item>
      <Form.Item
        name="waistCircumference"
        label="Waist circumference"
        labelAlign="left"
      >
        <InputNumber
          defaultValue={defaultValues?.waistCircumference}
          addonAfter="cm"
        />
        <Tooltip title="How to measure?">
          <Button
            type="primary"
            shape="circle"
            size="small"
            onClick={() => setWaistCircumferenceModal(true)}
            icon={<QuestionOutlined />}
            style={{
              margin: '5px 10px',
            }}
          />
        </Tooltip>
      </Form.Item>
      <Form.Item
        name="length"
        label="Height"
        labelAlign="left"
        rules={[{ required: true, message: 'Please input your height!' }]}
      >
        <InputNumber
          addonAfter="cm"
        />
      </Form.Item>
      <Form.Item
        name="isVegetarian"
        label="Vegetarian?"
        labelAlign="left"
        rules={[{ required: true, message: 'Please answer!' }]}
      >
        <Radio.Group>
          <Radio value={true}> Yes </Radio>
          <Radio value={false}> No </Radio>
        </Radio.Group>
      </Form.Item>
      <Form.Item {..._TAIL_FORM_ITEM_LAYOUT}>
        <Button type="primary" htmlType="submit" loading={updateLoading}>
          Save
        </Button>
      </Form.Item>
      <StyledModal
        open={waistCircumferenceModal}
        onOk={handleWaistCircumferenceModal}
        onCancel={handleWaistCircumferenceModal}
        footer={null}
        width={650}
      >
        <Row>
          <Col span={12}>
            <WaistCircumferenceImage src={waistCircumference} />
          </Col>
          <Col span={12}>
            <WaistCircumferenceTipInfo>
              <h3>How to measure your Waist Circumference?</h3>
              <ul>
                <li>Wrap a tape measure just above your hip bones and below the lowest rib.</li>
                <li>Hold the tape measure flat against body, not too tight.</li>
                <li>Stand up straight, exhale and take a reading.</li>
              </ul>
            </WaistCircumferenceTipInfo>
          </Col>
        </Row>
      </StyledModal>
    </Form>
  )
}

export default BioForm